import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MaterialModule } from 'src/app/material.module';
import { SharedModule } from '../../shared.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CompanyService } from 'src/app/services/apps/company/company.service';
import { EnvironmentService } from 'src/app/services/apps/environment/environment.service';
import { EquipmentService } from 'src/app/services/apps/equipment/equipment.service';
import { Company } from 'src/assets/types/interface/company.interface';
import { Environment } from 'src/assets/types/interface/environment.interface';
import { Equipment } from 'src/assets/types/interface/equipament.interface';
import { feedback_types_enum } from 'src/assets/types/enums/feedback-types.enum';
import { catchError, EMPTY, finalize, of } from 'rxjs';
import { CarouselComponent } from '../../carousel/carousel.component';

@Component({
  selector: 'app-equipment-modal',
  templateUrl: './equipment-modal.component.html',
  styleUrl: './equipment-modal.component.scss',
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    TablerIconsModule,
    CommonModule,
    SharedModule,
    CarouselComponent,
    ReactiveFormsModule,
  ],
})
export class EquipmentModalComponent {
  units: Company[] = [];
  environments: Environment[] = [];

  equipment: Equipment = {
    uuid: '',
    name: '',
    serial_number: '',
    description: '',
    environment: {
      uuid: '',
      name: '',
      company: {
        uuid: '',
        name: '',
      },
      details: '',
    },
  };

  equipmentForm: FormGroup;

  equipmentPhotos: any = [];
  photos: any = [];

  isEditing: boolean = false;
  currentImageIndex: number = 0;

  deletedPhotos: string[] = [];

  showAlert = false;
  showCancel = true;
  showConfirm = true;
  alertMessage = '';

  feedbackMessage = '';
  feedbackType = '';
  showFeedback = false;

  @ViewChild('fileInput') fileInput!: ElementRef;

  @ViewChild(CarouselComponent)
  carousel!: CarouselComponent;

  private alertResolve: ((value: boolean) => void) | null;

  constructor(
    private companyService: CompanyService,
    private environmentService: EnvironmentService,
    private equipmentService: EquipmentService,
    public dialogRef: MatDialogRef<EquipmentModalComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: Equipment,
  ) {
    this.equipment = data ?? this.equipment;
    this.isEditing = !!this.equipment.uuid;
  }

  ngOnInit() {
    this.loadCompanies();
    this.equipmentForm = this.fb.group({
      name: [this.equipment?.name ?? '', [Validators.required]],
      serial_number: [this.equipment?.serial_number ?? ''],
      description: [this.equipment?.description ?? ''],
      company_uuid: [
        this.equipment?.environment?.company?.uuid ?? '',
        Validators.required,
      ],
      environment_uuid: [
        { value: this.equipment?.environment?.uuid ?? '', disabled: true },
        Validators.required,
      ],
    });
  }

  loadCompanies() {
    this.companyService.getCompanies().subscribe({
      next: (data) => {
        this.units = data;
        this.loadEquipment();
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao carregar as empresas:', err);
      },
    });
  }

  loadEquipment() {
    const equipment$ = this.equipment?.uuid?.trim()
      ? this.equipmentService.getEquipment(this.equipment.uuid.trim())
      : EMPTY;

    equipment$
      .pipe(
        finalize(() => {
          this.loadEnvironments(this.equipment?.environment?.company?.uuid);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Erro ao carregar equipamento:', error);
          return of(null);
        }),
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.equipment = data;
            this.equipmentForm.patchValue({
              name: this.equipment.name,
              serial_number: this.equipment.serial_number,
              description: this.equipment.description,
              company_uuid: this.equipment.environment.company.uuid,
              environment_uuid: this.equipment.environment.uuid,
            });
          }
        },
      });
  }

  loadEnvironments(companyUuid: string | undefined) {
    if (!companyUuid) return;

    this.environmentService.getEnvironments(companyUuid).subscribe((res) => {
      this.environments = res;
      this.equipmentForm.get('environment_uuid')?.enable();
    });
  }

  onCompanyChange(companyUuid: string) {
    this.environments = [];
    this.equipmentForm.get('environment_uuid')?.reset();
    this.equipmentForm.get('environment_uuid')?.disable();
    this.loadEnvironments(companyUuid);
  }

  create() {
    const payload = this.buildPayload(true);

    this.equipmentService
      .createEquipment(this.equipmentForm.value.environment_uuid, payload)
      .subscribe({
        next: (res) => {
          this.carousel.upload(res.body?.uuid);
          this.dialogRef.close(res.body);
        },
        error: (err) => this.handleError(err, 'create'),
      });
  }

  update() {
    const payload = this.buildPayload(false);

    this.equipmentService
      .updateEquipment(this.equipment.uuid, payload)
      .subscribe({
        next: (res) => {
          this.carousel.upload();
          this.dialogRef.close(res.body);
        },
        error: (err) => this.handleError(err, 'update'),
      });
  }

  private buildPayload(isCreate: boolean) {
    return {
      name: this.equipmentForm.value.name,
      serialNumber: this.equipmentForm.value.serial_number,
      description: this.equipmentForm.value.description,
      environmentUuid: this.equipmentForm.value.environment_uuid,
    };
  }

  private handleError(err: any, action: 'create' | 'update') {
    const isCreate = action === 'create';

    this.feedbackMessage =
      err?.error?.message ||
      `Erro ao ${isCreate ? 'salvar' : 'editar'} o ambiente. Tente novamente.`;

    this.feedbackType = feedback_types_enum.error;
    this.showFeedback = true;

    console.error(`Erro ao ${isCreate ? 'salvar' : 'editar'} ambiente:`, err);

    setTimeout(() => (this.showFeedback = false), 3000);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  onAlertClosed(confirmed: boolean) {
    this.showAlert = false;
    if (this.alertResolve) {
      this.alertResolve(confirmed);
      this.alertResolve = null;
    }
  }
}
